import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Heart, TrendingUp, Wrench, Smartphone, GraduationCap, Shield, Users } from 'lucide-react';
import { FadeIn } from '@/components/Animations';

const GIGFLEET_LOGO = "https://customer-assets.emergentagent.com/job_5389659f-dea1-4240-af6c-b14886122f6e/artifacts/plrdoiq9_Gig%20Fleet%20Logo.png";
const BIKE_IMAGE = "https://customer-assets.emergentagent.com/job_csi-partnership-site/artifacts/48y6aysc_image.png";

const pillars = [
  {
    id: 'wecare',
    icon: Heart,
    title: 'WeCare',
    subtitle: 'Rider Wellbeing',
    color: 'bg-rose-500',
    lightColor: 'bg-rose-50',
    textColor: 'text-rose-600',
    description: 'A comprehensive support system that prioritizes rider safety and wellbeing.',
    features: [
      'Daily check-ins and engagement',
      'Safety awareness programs',
      'Accountability systems',
      'Community support network',
      'Mental health resources',
    ],
  },
  {
    id: 'thrive',
    icon: TrendingUp,
    title: 'Thrive',
    subtitle: 'Entrepreneurial Growth',
    color: 'bg-emerald-500',
    lightColor: 'bg-emerald-50',
    textColor: 'text-emerald-600',
    description: 'Structured programs that transform riders into successful entrepreneurs.',
    features: [
      'Business skills training',
      'Financial literacy programs',
      'Personal development courses',
      'Mentorship opportunities',
      'Career progression pathway',
    ],
  },
  {
    id: 'tools',
    icon: Wrench,
    title: 'Tools',
    subtitle: 'Technology Platform',
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    description: 'Cutting-edge technology that empowers riders to work smarter and earn more.',
    features: [
      'Gig Fleet mobile app',
      'AI-driven job assignments',
      'Real-time earnings tracking',
      'Training modules',
      'Business analytics dashboard',
    ],
  },
];

const SolutionPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="solution-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-emerald-50 via-slate-50 to-white">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <div className="space-y-6">
                <span className="inline-block px-4 py-2 bg-emerald-100 text-emerald-600 rounded-full text-sm font-semibold tracking-wide uppercase">
                  The Solution
                </span>
                <h1 className="text-4xl md:text-6xl font-heading font-bold text-slate-900">
                  A New Social Compact for Gigpreneurs
                </h1>
                <p className="text-xl text-slate-600 leading-relaxed">
                  We're not just identifying problems — we're building solutions. Our integrated 
                  model provides riders with the care, growth opportunities, and tools they need 
                  to succeed.
                </p>
                <div className="flex items-center gap-3 pt-4">
                  <span className="text-slate-500 text-sm">Powered by</span>
                  <img src={GIGFLEET_LOGO} alt="Gig Fleet Management" className="h-8 w-auto" />
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} direction="left">
              <div className="relative">
                <img
                  src={BIKE_IMAGE}
                  alt="MLM Branded Bike"
                  className="rounded-3xl shadow-xl w-full object-cover"
                />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Three Pillars */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container-wide">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-slate-900 mb-6">
                Three Pillars of Success
              </h2>
              <p className="text-lg text-slate-600">
                Our integrated approach addresses the complete rider journey — from safety and 
                wellbeing to growth and technology empowerment.
              </p>
            </div>
          </FadeIn>

          <div className="space-y-16">
            {pillars.map((pillar, index) => (
              <FadeIn key={pillar.id} delay={0.1 * index}>
                <div className={`rounded-3xl p-8 md:p-12 ${pillar.lightColor} border border-slate-100`}>
                  <div className="grid lg:grid-cols-2 gap-10 items-center">
                    <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                      <div className="flex items-center gap-4 mb-6">
                        <div className={`w-16 h-16 ${pillar.color} rounded-2xl flex items-center justify-center`}>
                          <pillar.icon className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h3 className="text-3xl font-heading font-bold text-slate-900">{pillar.title}</h3>
                          <p className={`${pillar.textColor} font-medium`}>{pillar.subtitle}</p>
                        </div>
                      </div>
                      <p className="text-lg text-slate-600 leading-relaxed mb-6">
                        {pillar.description}
                      </p>
                      <ul className="space-y-3">
                        {pillar.features.map((feature) => (
                          <li key={feature} className="flex items-center gap-3">
                            <div className={`w-6 h-6 ${pillar.lightColor} rounded-full flex items-center justify-center border ${pillar.textColor.replace('text', 'border')}`}>
                              <svg className={`w-4 h-4 ${pillar.textColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                            <span className="text-slate-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                      <div className="bg-white rounded-2xl p-8 shadow-lg">
                        <div className="grid grid-cols-2 gap-6">
                          {pillar.id === 'wecare' && (
                            <>
                              <div className="text-center p-4 bg-slate-50 rounded-xl">
                                <Shield className="w-8 h-8 text-rose-500 mx-auto mb-2" />
                                <p className="text-sm text-slate-600">Safety First</p>
                              </div>
                              <div className="text-center p-4 bg-slate-50 rounded-xl">
                                <Heart className="w-8 h-8 text-rose-500 mx-auto mb-2" />
                                <p className="text-sm text-slate-600">Wellbeing</p>
                              </div>
                              <div className="text-center p-4 bg-slate-50 rounded-xl">
                                <Users className="w-8 h-8 text-rose-500 mx-auto mb-2" />
                                <p className="text-sm text-slate-600">Community</p>
                              </div>
                              <div className="text-center p-4 bg-slate-50 rounded-xl">
                                <Shield className="w-8 h-8 text-rose-500 mx-auto mb-2" />
                                <p className="text-sm text-slate-600">Support</p>
                              </div>
                            </>
                          )}
                          {pillar.id === 'thrive' && (
                            <>
                              <div className="text-center p-4 bg-slate-50 rounded-xl">
                                <GraduationCap className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                                <p className="text-sm text-slate-600">Training</p>
                              </div>
                              <div className="text-center p-4 bg-slate-50 rounded-xl">
                                <TrendingUp className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                                <p className="text-sm text-slate-600">Growth</p>
                              </div>
                              <div className="text-center p-4 bg-slate-50 rounded-xl">
                                <Users className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                                <p className="text-sm text-slate-600">Mentorship</p>
                              </div>
                              <div className="text-center p-4 bg-slate-50 rounded-xl">
                                <Shield className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                                <p className="text-sm text-slate-600">Skills</p>
                              </div>
                            </>
                          )}
                          {pillar.id === 'tools' && (
                            <>
                              <div className="text-center p-4 bg-slate-50 rounded-xl">
                                <Smartphone className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                                <p className="text-sm text-slate-600">Mobile App</p>
                              </div>
                              <div className="text-center p-4 bg-slate-50 rounded-xl">
                                <Shield className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                                <p className="text-sm text-slate-600">AI Matching</p>
                              </div>
                              <div className="text-center p-4 bg-slate-50 rounded-xl">
                                <TrendingUp className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                                <p className="text-sm text-slate-600">Analytics</p>
                              </div>
                              <div className="text-center p-4 bg-slate-50 rounded-xl">
                                <GraduationCap className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                                <p className="text-sm text-slate-600">Learning</p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container-wide">
          <FadeIn>
            <div className="bg-gradient-to-br from-primary-mlm to-purple-700 rounded-3xl p-8 md:p-16 text-center">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
                Be Part of the Solution
              </h2>
              <p className="text-xl text-purple-100 max-w-2xl mx-auto mb-8">
                Whether you're a corporate partner, a rider, or an advocate — 
                there's a role for you in building a better gig economy.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  asChild
                  className="bg-white text-primary-mlm hover:bg-slate-100 rounded-full px-8 py-6 font-semibold"
                  data-testid="solution-partner-btn"
                >
                  <Link to="/corporate-csi">
                    Partner With Us
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button
                  asChild
                  className="bg-safety-yellow text-slate-900 hover:bg-yellow-400 rounded-full px-8 py-6 font-semibold"
                  data-testid="solution-rider-btn"
                >
                  <Link to="/for-riders">
                    I'm a Rider
                  </Link>
                </Button>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default SolutionPage;
